import React from "react";
import { Helmet } from "react-helmet";
import { ShieldCheck, Lock, Eye, Mail, Phone, MapPin, Calendar, Users, FileText } from "lucide-react";

const PrivacyPolicy = () => {
  const lastUpdated = "January 1, 2024";

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Helmet>
        <title>Privacy Policy | Knight Cleaning</title>
        <meta name="description" content="Privacy Policy for Knight Cleanings. Learn how we collect, use, and protect your personal information in compliance with Australian privacy laws." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-700 via-indigo-700 to-blue-800 text-white py-16 md:py-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent" />
        </div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <ShieldCheck size={18} />
              <span className="font-semibold">Your Privacy Matters</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Privacy <span className="bg-gradient-to-r from-amber-300 to-yellow-300 bg-clip-text text-transparent">Policy</span>
            </h1>
            <h2 className="text-2xl md:text-3xl font-semibold mb-4"> Understanding Your Privacy Rights with Knight Cleanings</h2>
            <p className="text-xl text-blue-100 mb-8">
              How we protect and manage your personal information at Knight Cleanings
            </p>
            <div className="flex items-center justify-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Calendar size={16} />
                <span>Last Updated: {lastUpdated}</span>
              </div>
              <div className="h-4 w-px bg-white/30" />
              <div className="flex items-center gap-2">
                <Users size={16} />
                <span>Applies to: Customers & Visitors</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 rounded-full bg-gradient-to-r from-blue-100 to-indigo-100 flex items-center justify-center">
                <FileText size={24} className="text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Introduction</h2>
                <p className="text-gray-600">Effective Date: {lastUpdated}</p>
              </div>
            </div>
            <p className="text-gray-700 mb-4">
              Knight Cleanings ("we", "us", "our") is committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy outlines how we collect, use, disclose, and manage your personal information in accordance with the Australian Privacy Principles (APPs) contained in the Privacy Act 1988 (Cth).
            </p>
            <p className="text-gray-700">
              By using our services, visiting our website, or providing information to us, you consent to the practices described in this Privacy Policy.
            </p>
          </div>

          {/* Quick Navigation */}
          <div className="bg-blue-50 rounded-xl p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Navigation</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { id: "collection", title: "Information We Collect" },
                { id: "use", title: "How We Use Your Information" },
                { id: "disclosure", title: "Information Disclosure" },
                { id: "security", title: "Security Measures" },
                { id: "access", title: "Your Rights" },
                { id: "cookies", title: "Cookies & Tracking" },
                { id: "children", title: "Children's Privacy" },
                { id: "changes", title: "Policy Updates" },
              ].map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  className="text-blue-600 hover:text-blue-800 hover:bg-blue-100 px-4 py-2 rounded-lg transition-colors"
                >
                  {item.title}
                </a>
              ))}
            </div>
          </div>

          {/* Policy Sections */}
          <div className="space-y-12">
            {/* 1. Information We Collect */}
            <section id="collection" className="scroll-mt-20">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Eye size={20} className="text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">1. Information We Collect</h2>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Personal Information</h3>
                  <p className="text-gray-700 mb-3">We collect personal information that you voluntarily provide to us, including:</p>
                  <ul className="list-disc pl-5 space-y-2 text-gray-700">
                    <li><strong>Contact Details:</strong> Name, email address, phone number, residential address</li>
                    <li><strong>Service Information:</strong> Property details, cleaning preferences, special requirements</li>
                    <li><strong>Payment Information:</strong> Billing address, payment method details (processed securely)</li>
                    <li><strong>Communication Records:</strong> Correspondence with our team via email, phone, or WhatsApp</li>
                    <li><strong>Booking Details:</strong> Service dates, times, frequency preferences</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Automatically Collected Information</h3>
                  <p className="text-gray-700 mb-3">When you visit our website, we may automatically collect:</p>
                  <ul className="list-disc pl-5 space-y-2 text-gray-700">
                    <li><strong>Technical Data:</strong> IP address, browser type, device information</li>
                    <li><strong>Usage Data:</strong> Pages visited, time spent, navigation patterns</li>
                    <li><strong>Location Data:</strong> General location (city/suburb level) for service area verification</li>
                    <li><strong>Cookies Data:</strong> Session cookies, preference cookies, security cookies</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Sensitive Information</h3>
                  <p className="text-gray-700">
                    We do not intentionally collect sensitive information (such as racial or ethnic origin, political opinions, religious beliefs, health information) unless:
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-2 text-gray-700">
                    <li>You voluntarily provide it to us for a specific purpose (e.g., health-related cleaning requirements)</li>
                    <li>It is required by law</li>
                    <li>You have given explicit consent</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* 2. How We Use Your Information */}
            <section id="use" className="scroll-mt-20">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                  <Mail size={20} className="text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">2. How We Use Your Information</h2>
              </div>
              
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="font-semibold text-gray-900 mb-3">Primary Purposes</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <div className="h-5 w-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 flex-shrink-0">
                          <div className="h-2 w-2 rounded-full bg-blue-600" />
                        </div>
                        <span>Providing and managing cleaning services</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="h-5 w-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 flex-shrink-0">
                          <div className="h-2 w-2 rounded-full bg-blue-600" />
                        </div>
                        <span>Processing bookings and payments</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="h-5 w-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 flex-shrink-0">
                          <div className="h-2 w-2 rounded-full bg-blue-600" />
                        </div>
                        <span>Communicating about your service</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="h-5 w-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 flex-shrink-0">
                          <div className="h-2 w-2 rounded-full bg-blue-600" />
                        </div>
                        <span>Sending service confirmations and reminders</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="font-semibold text-gray-900 mb-3">Secondary Purposes</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mt-0.5 flex-shrink-0">
                          <div className="h-2 w-2 rounded-full bg-green-600" />
                        </div>
                        <span>Improving our services and customer experience</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mt-0.5 flex-shrink-0">
                          <div className="h-2 w-2 rounded-full bg-green-600" />
                        </div>
                        <span>Sending marketing communications (with consent)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mt-0.5 flex-shrink-0">
                          <div className="h-2 w-2 rounded-full bg-green-600" />
                        </div>
                        <span>Conducting business analytics and research</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mt-0.5 flex-shrink-0">
                          <div className="h-2 w-2 rounded-full bg-green-600" />
                        </div>
                        <span>Complying with legal obligations</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-xl p-6">
                  <h3 className="font-semibold text-blue-900 mb-3">Legal Basis for Processing</h3>
                  <p className="text-blue-800 mb-3">We process your personal information based on one or more of the following legal grounds:</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center mx-auto mb-2">
                        <span className="font-bold text-blue-600">A</span>
                      </div>
                      <p className="text-sm text-blue-800">Your explicit consent</p>
                    </div>
                    <div className="text-center">
                      <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center mx-auto mb-2">
                        <span className="font-bold text-blue-600">B</span>
                      </div>
                      <p className="text-sm text-blue-800">Contractual necessity</p>
                    </div>
                    <div className="text-center">
                      <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center mx-auto mb-2">
                        <span className="font-bold text-blue-600">C</span>
                      </div>
                      <p className="text-sm text-blue-800">Legitimate business interests</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* 3. Information Disclosure */}
            <section id="disclosure" className="scroll-mt-20">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <Users size={20} className="text-purple-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">3. Information Disclosure</h2>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">When We Share Your Information</h3>
                  <p className="text-gray-700 mb-4">We may disclose your personal information to third parties in the following circumstances:</p>
                  
                  <div className="space-y-4">
                    <div className="bg-white border border-gray-200 rounded-xl p-5">
                      <h4 className="font-semibold text-gray-900 mb-2">Service Providers</h4>
                      <p className="text-gray-700 text-sm">
                        Trusted partners who assist in delivering our services (e.g., payment processors, scheduling software providers, subcontractors under confidentiality agreements)
                      </p>
                    </div>
                    
                    <div className="bg-white border border-gray-200 rounded-xl p-5">
                      <h4 className="font-semibold text-gray-900 mb-2">Legal Requirements</h4>
                      <p className="text-gray-700 text-sm">
                        When required by law, court order, or government authorities to comply with legal obligations
                      </p>
                    </div>
                    
                    <div className="bg-white border border-gray-200 rounded-xl p-5">
                      <h4 className="font-semibold text-gray-900 mb-2">Business Transfers</h4>
                      <p className="text-gray-700 text-sm">
                        In connection with a merger, acquisition, or sale of assets, where personal information may be transferred as a business asset
                      </p>
                    </div>
                    
                    <div className="bg-white border border-gray-200 rounded-xl p-5">
                      <h4 className="font-semibold text-gray-900 mb-2">With Your Consent</h4>
                      <p className="text-gray-700 text-sm">
                        When you have given explicit consent for specific disclosures
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                  <h3 className="font-semibold text-yellow-900 mb-3">International Transfers</h3>
                  <p className="text-yellow-800">
                    We primarily store and process data within Australia. If we transfer your information outside Australia, we ensure appropriate safeguards are in place and comply with Australian privacy laws.
                  </p>
                </div>
              </div>
            </section>

            {/* 4. Security Measures */}
            <section id="security" className="scroll-mt-20">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                  <Lock size={20} className="text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">4. Security Measures</h2>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Our Security Commitment</h3>
                  <p className="text-gray-700 mb-6">
                    We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="h-16 w-16 rounded-full bg-gradient-to-r from-blue-100 to-indigo-100 flex items-center justify-center mx-auto mb-4">
                        <ShieldCheck size={24} className="text-blue-600" />
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Technical Security</h4>
                      <p className="text-sm text-gray-600">SSL encryption, firewalls, secure servers, regular security updates</p>
                    </div>
                    
                    <div className="text-center">
                      <div className="h-16 w-16 rounded-full bg-gradient-to-r from-green-100 to-emerald-100 flex items-center justify-center mx-auto mb-4">
                        <Lock size={24} className="text-green-600" />
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Access Controls</h4>
                      <p className="text-sm text-gray-600">Role-based access, strong authentication, employee training</p>
                    </div>
                    
                    <div className="text-center">
                      <div className="h-16 w-16 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 flex items-center justify-center mx-auto mb-4">
                        <Eye size={24} className="text-purple-600" />
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Data Management</h4>
                      <p className="text-sm text-gray-600">Regular audits, data minimization, retention policies</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Data Breach Response</h3>
                  <p className="text-gray-700 mb-3">In the unlikely event of a data breach, we will:</p>
                  <ul className="list-disc pl-5 space-y-2 text-gray-700">
                    <li>Immediately investigate and contain the breach</li>
                    <li>Assess the risk and impact</li>
                    <li>Notify affected individuals and the Office of the Australian Information Commissioner (OAIC) if required</li>
                    <li>Implement measures to prevent future breaches</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* 5. Your Rights */}
            <section id="access" className="scroll-mt-20">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-full bg-teal-100 flex items-center justify-center">
                  <FileText size={20} className="text-teal-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">5. Your Rights</h2>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Privacy Rights Under Australian Law</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {[
                      {
        title: "Access & Correction",
        description: "You have the right to access and correct your personal information held by us",
        icon: "👁️"
      },
      {
        title: "Opt-Out",
        description: "You can opt-out of marketing communications at any time using the unsubscribe link",
        icon: "📧"
      },
      {
        title: "Data Portability",
        description: "Request a copy of your data in a structured, commonly used format",
        icon: "📤"
      },
      {
        title: "Deletion",
        description: "Request deletion of your personal information, subject to legal requirements",
        icon: "🗑️"
      },
      {
        title: "Restriction",
        description: "Request restriction of processing in certain circumstances",
        icon: "⏸️"
      },
      {
        title: "Objection",
        description: "Object to processing based on legitimate interests",
        icon: "🚫"
      }
                    ].map((right, index) => (
                      <div key={index} className="bg-white border border-gray-200 rounded-xl p-5">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-2xl">{right.icon}</span>
                          <h4 className="font-semibold text-gray-900">{right.title}</h4>
                        </div>
                        <p className="text-gray-700 text-sm">{right.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-blue-50 rounded-xl p-6">
                  <h3 className="font-semibold text-blue-900 mb-3">How to Exercise Your Rights</h3>
                  <p className="text-blue-800 mb-4">
                    To exercise any of your privacy rights, please contact us using the details below. We will respond to your request within 30 days.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Mail size={18} className="text-blue-600" />
                      <span className="text-blue-800">Email: privacy@knightcleanings.com.au</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone size={18} className="text-blue-600" />
                      <span className="text-blue-800">Phone: +61 420 499 661</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <MapPin size={18} className="text-blue-600" />
                      <span className="text-blue-800">Mail: Privacy Officer, Knight Cleanings, Melbourne VIC</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* 6. Cookies & Tracking */}
            <section id="cookies" className="scroll-mt-20">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <div className="h-5 w-5 rounded-full bg-amber-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">6. Cookies & Tracking Technologies</h2>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Types of Cookies We Use</h3>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">Cookie Type</th>
                          <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">Purpose</th>
                          <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">Duration</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-gray-200 px-4 py-3 text-gray-700">Essential Cookies</td>
                          <td className="border border-gray-200 px-4 py-3 text-gray-700">Required for website functionality and security</td>
                          <td className="border border-gray-200 px-4 py-3 text-gray-700">Session</td>
                        </tr>
                        <tr>
                          <td className="border border-gray-200 px-4 py-3 text-gray-700">Analytics Cookies</td>
                          <td className="border border-gray-200 px-4 py-3 text-gray-700">Help us understand website usage and improve services</td>
                          <td className="border border-gray-200 px-4 py-3 text-gray-700">1-2 years</td>
                        </tr>
                        <tr>
                          <td className="border border-gray-200 px-4 py-3 text-gray-700">Preference Cookies</td>
                          <td className="border border-gray-200 px-4 py-3 text-gray-700">Remember your settings and preferences</td>
                          <td className="border border-gray-200 px-4 py-3 text-gray-700">1 year</td>
                        </tr>
                        <tr>
                          <td className="border border-gray-200 px-4 py-3 text-gray-700">Marketing Cookies</td>
                          <td className="border border-gray-200 px-4 py-3 text-gray-700">Track advertising effectiveness (with consent)</td>
                          <td className="border border-gray-200 px-4 py-3 text-gray-700">6 months</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Cookie Management</h3>
                  <p className="text-gray-700 mb-4">
                    You can control and manage cookies in various ways:
                  </p>
                  <ul className="list-disc pl-5 space-y-2 text-gray-700">
                    <li><strong>Browser Settings:</strong> Most browsers allow you to refuse or delete cookies</li>
                    <li><strong>Cookie Consent Banner:</strong> Use our cookie consent tool on the website</li>
                    <li><strong>Opt-Out Tools:</strong> Use industry opt-out tools for advertising cookies</li>
                    <li><strong>Do Not Track:</strong> Enable "Do Not Track" settings in your browser</li>
                  </ul>
                  <p className="text-gray-700 mt-4 text-sm">
                    <strong>Note:</strong> Disabling essential cookies may affect website functionality.
                  </p>
                </div>
              </div>
            </section>

            {/* 7. Children's Privacy */}
            <section id="children" className="scroll-mt-20">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-full bg-pink-100 flex items-center justify-center">
                  <span className="text-pink-600 font-bold">C</span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">7. Children's Privacy</h2>
              </div>
              
              <div className="space-y-6">
                <div className="bg-pink-50 rounded-xl p-6">
                  <h3 className="font-semibold text-pink-900 mb-3">Our Policy Regarding Children</h3>
                  <p className="text-pink-800 mb-3">
                    Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children under 18.
                  </p>
                  <p className="text-pink-800">
                    If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately. We will take steps to delete such information from our records.
                  </p>
                </div>
              </div>
            </section>

            {/* 8. Policy Updates */}
            <section id="changes" className="scroll-mt-20">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                  <Calendar size={20} className="text-indigo-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">8. Policy Updates & Contact</h2>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Changes to This Policy</h3>
                  <p className="text-gray-700 mb-4">
                    We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. The updated policy will be posted on our website with the effective date.
                  </p>
                  <div className="bg-gray-50 rounded-xl p-5">
                    <h4 className="font-semibold text-gray-900 mb-2">Notification of Changes</h4>
                    <p className="text-gray-700 text-sm">
                      For significant changes, we will notify you by email or through a prominent notice on our website at least 30 days before the changes take effect.
                    </p>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Contact Information</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Privacy Officer</h4>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <Mail size={18} className="text-blue-600" />
                          <span className="text-gray-700">privacy@knightcleanings.com.au</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <Phone size={18} className="text-blue-600" />
                          <span className="text-gray-700">+61 420 499 661</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <MapPin size={18} className="text-blue-600" />
                          <span className="text-gray-700">Knight Cleanings, Melbourne VIC, Australia</span>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Regulatory Authority</h4>
                      <p className="text-gray-700 mb-3">
                        If you have concerns about our privacy practices, you may contact:
                      </p>
                      <div className="space-y-2">
                        <p className="text-sm text-gray-600"><strong>Office of the Australian Information Commissioner (OAIC)</strong></p>
                        <p className="text-sm text-gray-600">Website: oaic.gov.au</p>
                        <p className="text-sm text-gray-600">Phone: 1300 363 992</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Final Note */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="bg-gradient-to-r from-gray-50 to-white rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Your Trust is Important to Us</h3>
              <p className="text-gray-700 mb-4">
                At Knight Cleanings, we value your trust and are committed to protecting your privacy. This Privacy Policy reflects our dedication to transparency and responsible data management.
              </p>
              <p className="text-gray-700">
                By using our services, you acknowledge that you have read and understood this Privacy Policy. Thank you for choosing Knight Cleanings.
              </p>
            </div>
          </div>
        </div>

        {/* Download Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Download Privacy Policy</h3>
              <p className="text-gray-600">Save a copy of our Privacy Policy for your records</p>
            </div>
            <div className="flex gap-4">
              <button className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-300">
                Download PDF
              </button>
              <button className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                Print Version
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;